import React, { useCallback, useContext, useEffect, useState } from "react";
import "./style.scss";
import { Input, message, Spin } from "antd";
import Button from "../../../common/Button";
import { useDropzone } from "react-dropzone";
import uploadImage from "../../../assets/CreateCategory/upload.svg";
import CustomDropZone from "../../../common/CustomDropZone";
import dividerImage from "../../../assets/CreateCategory/divider.svg";
import { useCreateNewSubCategory } from "../../../hooks/useGuidelines";
import guidelinesApi from "../../../apis/guidelines";
import { GuidelinesSelectedCategoryContext } from "../../../context/guidelinesProvider";

const CreateSubCategory = ({
  handleModalClose,
  isSubCategoryCreated,
  onSubCategoryCreated,
}) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { getSubCategoryPresignedUrl, getThumbPathOfImage } = guidelinesApi;
  const [fileUpload, setFileUpload] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isImageLoading, setIsImageLoading] = useState(true);
  const { guide_selectedCategoryName, guide_selectedCategoryId } = useContext(
    GuidelinesSelectedCategoryContext
  );

  const onSuccessCreateSubCategory = (res) => {
    if (res.status == 200) {
      message.success("Sub Category successfully created");
      if (onSubCategoryCreated) {
        onSubCategoryCreated(formInput?.subCategoryName);
      }
      setTimeout(() => {
        isSubCategoryCreated();
      });
    }
  };
  const onErrorCreateSubCategory = () => {};

  const { mutate: createNewSubcategory } = useCreateNewSubCategory(
    onSuccessCreateSubCategory,
    onErrorCreateSubCategory
  );

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const [formInput, setFormInput] = useState({
    subCategoryName: "",
    categoryImage: "",
    reason: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const allowedExtensions = ["jpg", "jpeg", "png", "webp"];

  const validateForm = () => {
    const newErrors = {};

    if (!formInput?.subCategoryName?.trim()) {
      newErrors.subCategoryName = "Sub Category name is required.";
    }

    if (!formInput?.categoryImage) {
      newErrors.categoryImage = "Sub Category image is required.";
    }

    if (!formInput?.reason?.trim()) {
      newErrors.reason = "Reason is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getKeyPresignedUrl = async (file) => {
    let uploadSuccess = true;
    setIsSuccess(true);
    try {
      if (!file[0].name || !file[0].type) {
        uploadSuccess = false;
        setIsSuccess(false);
        throw new Error(
          "Invalid file detected. Ensure the file has a valid name and type."
        );
      }

      const resp = await getSubCategoryPresignedUrl({
        filename: file[0].name,
        mimeType: file[0].type,
      });

      if (resp?.status !== 200 || !resp?.data?.key || !resp?.data?.url) {
        uploadSuccess = false;
        setIsSuccess(false);
        throw new Error("Invalid response from getPresignedUrl.");
      }

      const { key, url } = resp.data;

      const uploadResp = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file[0].type, // Set the correct content type
        },
        body: file[0], // Send the file data
      });

      if (!uploadResp.ok) {
        uploadSuccess = false;
        setIsSuccess(false);

        throw new Error(`File upload failed with status: ${uploadResp.status}`);
      }

      if (uploadSuccess) {
        setIsSuccess(true);
        getThumbPathOfImage({
          key,
        }).then((resp) => {
          setImageUrl(resp?.data?.data?.thumbPath?.url);
        });
        const addFilesPayload = {
          image: {
            imagePath: {
              key,
            },
          },
        };

        setFileUpload(addFilesPayload);
        setIsSuccess(true);
        message.success("Image uploaded successfully.");
      } else {
        message.error("Some images failed to upload.");
      }
    } catch (err) {
      uploadSuccess = false;
      setIsSuccess(false);
      message.error(
        err?.response?.data?.message ||
          err.message ||
          "An error occurred during upload."
      );
    } finally {
      setIsUploadingImage(false);
    }
    return uploadSuccess;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpg": "",
      "image/png": "",
      "image/jpeg": "",
      "image/webp": "",
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setImageUrl("");
        setFileUpload({});
        setFormInput((prev) => ({
          ...prev,
          categoryImage: acceptedFiles[0],
        }));
        setIsUploadingImage(true);
        setIsDragging(false);
        getKeyPresignedUrl(acceptedFiles);
      }
    },
    multiple: false,
  });
  const onDropItems = async (file) => {
    if (file.length > 0) {
      setImageUrl("");
      setFileUpload({});
      setFormInput((prev) => ({
        ...prev,
        categoryImage: file[0],
      }));
      setIsUploadingImage(true);
      setIsDragging(false);

      getKeyPresignedUrl(file);
    }
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const renderDragAndDropSection = useCallback(
    () =>
      isDragging && (
        <CustomDropZone
          dropZoneClass={"uploaded_images_row"}
          onDropItems={onDropItems}
          isShowTextOnDrag={true}
          acceptingFormates={{
            "image/jpg": "",
            "image/png": "",
            "image/jpeg": "",
            "image/webp": "",
          }}
          handleDragLeave={handleDragLeave}
          multiple={false}
        />
      ),
    [isDragging, onDropItems, handleDragLeave]
  );

  const getUploadWrapper = () => {
    return (
      <>
        {isSuccess ? (
          <div className="upload_image_wrapper">
            {imageUrl ? (
              <>
                {isImageLoading && (
                  <div className="image-placeholder">Loading...</div>
                )}
                <img
                  src={imageUrl}
                  alt="Uploaded"
                  style={{ display: isImageLoading ? "none" : "block" }}
                  onLoad={handleImageLoad}
                />
              </>
            ) : (
              ""
            )}
            <div
              className="cross-icon"
              onClick={() => {
                setIsSuccess(false);
                setFormInput((prev) => ({
                  ...prev,
                  categoryImage: "",
                }));
              }}
            ></div>
          </div>
        ) : (
          <div className="upload_wrapper">
            <div className="category_image">
              <img src={uploadImage} alt="" />
              <p>Drag your image</p>
            </div>
            <div className="or_image">
              <img src={dividerImage} alt="" />
            </div>
            <label className="browse" onClick={handleAreaClick}>
              Browse files
            </label>
            <input
              type="file"
              accept="image/png,image/jpg,image/jpeg, image/webp"
              id="file"
              onChange={(e) => handleUploadImage(e.target.files)}
              onClick={(e) => (e.target.value = null)}
              className="upload_more_images_btn_input"
            />
          </div>
        )}
      </>
    );
  };

  const handleUploadImage = async (file) => {
    if (file.length > 0) {
      setImageUrl("");
      setFileUpload({});
      setFormInput((prev) => ({
        ...prev,
        categoryImage: file[0],
      }));
      setIsUploadingImage(true);
      getKeyPresignedUrl(file);
    }
  };

  const handleAreaClick = () => {
    document.getElementById("file")?.click();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      // setIsSubmitted(true);
      let payload = {
        name: formInput?.subCategoryName,
        reason: formInput?.reason,
        ...fileUpload,
        category: guide_selectedCategoryId,
        isShootable: "true",
      };
      createNewSubcategory(payload);
      setFormInput({
        subCategoryName: "",
        categoryImage: "",
        reason: "",
      });
      setErrors({});
    } else {
      message.error("Please fix the errors in the form.");
    }
  };
  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    let foundImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.includes("image")) {
        foundImage = true;
        const blob = item.getAsFile();

        if (blob) {
          const file = new File(
            [blob],
            `pasted-image.${blob.type.split("/")[1]}`,
            { type: blob.type }
          );

          setImageUrl("");
          setFileUpload({});
          setFormInput((prev) => ({
            ...prev,
            categoryImage: file,
          }));
          setIsUploadingImage(true);
          getKeyPresignedUrl([file]);
        }
      }
    }

    if (!foundImage) {
      message.error("Pasted content is not an image.");
    }
  };
  return (
    <div className="create_category_main_wrapper" onDragEnter={handleDragEnter}>
      <div className="create_category_content">
        <form onSubmit={handleSubmit}>
          <div className="form_wrapper">
            <div className="form_control">
              <label>
                Category Name <sup>*</sup>
              </label>
              <input
                placeholder="Enter Category Name"
                name="categoryName"
                disabled={true}
                value={guide_selectedCategoryName}
                className="category_name"
              />
            </div>
            <div className="form_control">
              <label>
                Sub category name <sup>*</sup>
              </label>
              <input
                placeholder="Enter Category Name"
                name="subCategoryName"
                value={formInput.subCategoryName}
                onChange={handleChange}
              />
              {errors.categoryName && (
                <p className="error_message">{errors.subCategoryName}</p>
              )}
            </div>
          </div>
          <div className="form_control">
            <label>
              Sub category image <sup>*</sup>
            </label>
            <div className="drop_down_wrapper" onPaste={handlePaste}>
              <div className="item_upload_wrapper">
                {isUploadingImage ? <Spin /> : getUploadWrapper()}
                {renderDragAndDropSection()}
              </div>
            </div>
            {errors.categoryImage && (
              <p className="error_message">{errors.categoryImage}</p>
            )}
          </div>
          <div className="form_control">
            <label>
              Reason for sub category creation <sup>*</sup>
            </label>
            <textarea
              placeholder="Write your reason here..."
              name="reason"
              value={formInput.reason}
              onChange={handleChange}
            />
            {errors.reason && <p className="error_message">{errors.reason}</p>}
          </div>
          <div className="action_wrapper">
            <button className="cancelBtn" onClick={handleModalClose}>
              {" "}
              Cancel
            </button>
            <Button
              width="264px"
              height="57px"
              text="Send for Approval"
              className="approvalBtn"
              isDisabled={
                !formInput.subCategoryName ||
                !formInput.categoryImage ||
                !formInput.reason
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};
export default CreateSubCategory;
